// JWT token management. Stored in localStorage — vulnerable to XSS but immune
// to CSRF (since we never auth via cookies). Mitigations: aggressive CSP set
// by the Worker, and React's default escaping for any rendered API data.
//
// NEVER add code that interpolates API data into raw HTML. If you ever need
// rich text rendering for descriptions/notes, use a markdown library that
// escapes by default (e.g. react-markdown) — never bypass React's escaping.

const TOKEN_KEY = "babel.token";
const ROLE_KEY = "babel.role";
const EXPIRES_KEY = "babel.expires_at";

export type Role = "admin" | "scan";

export interface Session {
  token: string;
  role: Role;
  expiresAt: number;
}

export function getSession(): Session | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const role = localStorage.getItem(ROLE_KEY);
  const expiresAt = localStorage.getItem(EXPIRES_KEY);
  if (!token || !role || !expiresAt) return null;
  if (role !== "admin" && role !== "scan") return null;
  const exp = Number(expiresAt);
  if (!Number.isFinite(exp)) return null;
  // Reject expired tokens up front so we don't make doomed requests.
  if (Date.now() / 1000 > exp) {
    clearSession();
    return null;
  }
  return { token, role, expiresAt: exp };
}

export function setSession(session: Session): void {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(ROLE_KEY, session.role);
  localStorage.setItem(EXPIRES_KEY, String(session.expiresAt));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

/**
 * Server-side logout: bumps `users.token_version`, revoking every JWT
 * currently issued to this user (other tabs, other devices). Best-effort —
 * the local session is cleared regardless of network outcome so a flaky
 * connection doesn't strand the user.
 */
export async function logout(apiUrl: string): Promise<void> {
  const session = getSession();
  if (session) {
    try {
      await fetch(`${apiUrl}/auth/logout`, {
        method: "POST",
        headers: { authorization: `Bearer ${session.token}` },
      });
    } catch {
      // Network failure: still clear locally below.
    }
  }
  clearSession();
}

/**
 * Default landing route by role. Roommate (`scan`) lands on /scan; admin lands
 * on /lookup. The router enforces this independently — `scan` users get 403'd
 * server-side from /lookup endpoints, but we also hide the link client-side.
 */
export function defaultRouteForRole(role: Role): string {
  return role === "admin" ? "/lookup" : "/scan";
}
